import {
  Box,
  HStack,
  Heading,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
  VStack,
  useBreakpointValue
} from "@chakra-ui/react"
import { AvatarCircleIcon } from "@pathwright/pathicons"
import { augmentationSectionFocusTarget } from "@pathwright/ux/src/components/augmentation/sectionFocus"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import { AvatarUser } from "@pathwright/ux/src/components/avatar/types"
import {
  CarouselGallery,
  GridGallery
} from "@pathwright/ux/src/components/gallery/Gallery"
import Link from "@pathwright/ux/src/components/link/Link"
import HTMLText from "@pathwright/ux/src/components/text/HTMLText"
import { Fragment, useEffect, useState } from "react"
import { CoverPageContributorFragment } from "@pathwright/graphql-client/src/generated"
import CoverPageDivider from "../CoverPageDivider"
import { useCoverPageContext } from "../CoverPageProvider"
import { useCoverPageIsEditable } from "../access"
import { COVER_PAGE_PADDING } from "../constants"
import { useCoverPageBlankSlate } from "../design/useCoverPageBlankSlate"
import Placeholder from "@pathwright/ux/src/components/placeholder/Placeholder"
import {
  CoverPageAddAnother,
  CoverPageBlankSlateSection
} from "../design/CoverPageBlankSlate"
import CoverPageSectionTrigger from "../design/CoverPageSectionTrigger"
import { ImmersiveTriggerButton } from "../immersive/CoverPageImmersive"
import { CoverPageContextType } from "../types"
import { useCoverPageTranslate } from "../utils"
import { getAvatarUserFromContributor } from "./utils"

const ContributorBio = ({
  bio,
  truncate = true,
  id,
  maxHeight,
  onTruncationChange
}: {
  bio: string
  maxHeight?: number
  id?: number
  /** When true, mounts TruncatedHTML (line clamp + “more”). Use onTruncationChange for layout that depends on whether content is clipped. */
  truncate?: boolean
  onTruncationChange?: (truncated: boolean) => void
}) => {
  return (
    <HTMLText
      html={bio}
      color="PRIMARY_GRAY"
      w="100%"
      truncate={truncate}
      truncateProps={{
        maxHeight,
        ellipsis: true,
        renderMore: () => (
          <ImmersiveTriggerButton relativeTo={`contributor/${id}`} />
        ),
        onMeasure: (info) => onTruncationChange?.(info.truncated)
      }}
    />
  )
}

const AVATAR_SIZE = "150px"

// Note: "authors" have been renamed to "contributors" on the frontend
const ContributorCard = ({
  contributor,
  full,
  disableTruncation
}: {
  contributor: CoverPageContributorFragment
  full?: boolean
  disableTruncation?: boolean
}) => {
  const isEditable = useCoverPageIsEditable()

  const canTruncate = !disableTruncation

  // Drives card chrome (e.g. row align). Source of truth is TruncatedHTML onMeasure — not
  // wrapper scrollHeight, which misses overflow when truncate was off or children fill height.
  const [isTruncated, setIsTruncated] = useState(false)
  const { isMobile, immersiveActive } = useCoverPageContext()

  useEffect(() => {
    if (!canTruncate || !contributor.bio) {
      setIsTruncated(false)
    }
  }, [canTruncate, contributor.bio])

  const cardContentHeight = full && !isMobile ? "100%" : "auto"

  return (
    <LinkBox asChild>
      <Stack
        className="ContributorCard"
        {...augmentationSectionFocusTarget("surface")}
        direction={full ? "row" : "column"}
        align={isTruncated ? "center" : "start"}
        gap={full ? "30px" : 2}
        h="100%"
        w="100%"
        maxW={full || isMobile ? "100%" : "200px"}
        key={contributor.id}
        overflow={full ? "hidden" : "visible"}
        px={4}
        pt={full ? 4 : 2}
        pb={full ? 4 : 2}
        cursor={immersiveActive ? "default" : "pointer"}
        // Always rounded (invisible while transparent) so the corners can't snap
        // square ahead of the background/shadow fading out.
        borderRadius="card"
        _hover={{
          bgColor: "white",
          boxShadow: "card"
        }}
      >
        <UserAvatar
          className="ContributorAvatar"
          user={getAvatarUserFromContributor(contributor) as AvatarUser}
          size="2xl"
          w={AVATAR_SIZE}
          maxW={AVATAR_SIZE}
          h={AVATAR_SIZE}
          maxH={AVATAR_SIZE}
          icon={<AvatarCircleIcon style={{ marginRight: "5px" }} />}
        />
        <VStack
          gap={0}
          className="ContributorCardContent"
          w="100%"
          align={"start"}
          h={cardContentHeight}
        >
          <VStack gap={0} w="100%" align={"start"}>
            <LinkOverlay asChild>
              <Link
                relativeTo={
                  immersiveActive ? "" : `contributor/${contributor.id}/`
                }
                textDecor="none"
              >
                <Heading
                  as="h3"
                  // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
                  size="h4"
                  mt={0}
                  mb={full ? "5px" : 0}
                  color="PRIMARY_GRAY"
                  fontWeight="600"
                  textAlign={"left"}
                  position="relative"
                >
                  {contributor.salutation ? `${contributor.salutation} ` : ""}
                  {contributor.full_name}
                  {isEditable && (
                    <Box
                      position="absolute"
                      right={"-40px"}
                      top={"-4px"}
                      css={{
                        "& .CoverPageSectionTriggerLink": {
                          opacity: 0
                        },
                        ".ContributorCardContent:hover & .CoverPageSectionTriggerLink":
                          {
                            opacity: 1
                          }
                      }}
                    >
                      <CoverPageSectionTrigger
                        sectionKey="contributors"
                        panelItemId={contributor.id}
                        includeLabel={false}
                        top={0}
                        right={0}
                        preventScrollHighlight
                        revealWithin=".ContributorCardContent"
                        itemName={contributor.display_name}
                      />
                    </Box>
                  )}
                </Heading>
              </Link>
            </LinkOverlay>
            {contributor.title && (
              <Text
                mt={0}
                mb="5px"
                color="gray.500"
                textAlign={"left"}
                cursor="text"
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
              >
                {contributor.title}
              </Text>
            )}
          </VStack>
          {contributor.bio && (
            <Box
              css={{
                "& p:first-of-type": {
                  marginTop: "0"
                },
                "& p:last-of-type": {
                  marginBottom: "0"
                }
              }}
              position="relative"
              w="100%"
              h="100%"
              cursor="text"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
              textAlign="left"
              maxH={!canTruncate ? "auto" : `calc(1.4rem * 4)`}
            >
              <ContributorBio
                bio={contributor.bio!}
                truncate={canTruncate}
                onTruncationChange={setIsTruncated}
                // 1.4 (line height) * 16 (font size) * 4 (lines)
                maxHeight={!canTruncate ? 0 : 1.4 * 16 * 4}
                id={contributor.id as number}
              />
            </Box>
          )}
        </VStack>
      </Stack>
    </LinkBox>
  )
}

const ContributorGridCard = ({
  contributor
}: {
  contributor: CoverPageContributorFragment
}) => {
  return (
    <VStack
      className="ContributorGridCard"
      align="stretch"
      borderRadius="card"
      bgColor="backgroundSecondary"
      boxShadow="card"
      overflow="hidden"
      p={"20px"}
      h="100%"
      css={{
        "& .ContributorCard": {
          w: "100%",
          maxW: "100%",
          bgColor: "backgroundSecondary",
          borderRadius: "card",
          boxShadow: "card"
        },
        "& .ContributorAvatar": {
          maxW: "100px",
          maxH: "100px"
        }
      }}
    >
      <HStack h="100px">
        <UserAvatar
          className="ContributorAvatar"
          user={getAvatarUserFromContributor(contributor) as AvatarUser}
          size="2xl"
          w="100px"
          maxW="100px"
          h="100px"
          maxH="100px"
          icon={<AvatarCircleIcon style={{ marginRight: "5px" }} />}
        />
        <VStack align="start" gap={0}>
          <Heading
            as="h3"
            // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
            size="h4"
            mt={0}
            mb={0}
            color="PRIMARY_GRAY"
            fontWeight="600"
            textAlign={"left"}
          >
            {contributor.full_name}
          </Heading>
          <Text>{contributor.title}</Text>
        </VStack>
      </HStack>
      <Box w="100%">
        <ContributorBio
          bio={contributor.bio!}
          truncate
          maxHeight={1.4 * 16 * 6}
          id={contributor.id as number}
        />
      </Box>
    </VStack>
  )
}

const ContributorCardStack = () => {
  const { contributors, isMobile } = useCoverPageContext()

  return (
    <VStack
      align={isMobile ? "center" : "start"}
      gap={2}
      px={COVER_PAGE_PADDING}
    >
      {contributors.map(
        (contributor: CoverPageContributorFragment, index: number) => (
          <Fragment key={contributor.id}>
            <ContributorCard
              key={contributor.id}
              contributor={contributor}
              full={!isMobile}
            />
            {index !== contributors.length - 1 && (
              <CoverPageDivider
                horizontalMargin={"0"}
                verticalMargin={"20px"}
              />
            )}
          </Fragment>
        )
      )}
    </VStack>
  )
}

const CoverPageContributors = () => {
  const missingContributors = useCoverPageBlankSlate("contributors")
  const { t } = useCoverPageTranslate()
  const { contributors, contributorSectionRef, immersiveActive } =
    useCoverPageContext()
  const isMobile = useCoverPageContext((v: CoverPageContextType) => v.isMobile)

  const itemsPerRow = useBreakpointValue({
    base: 1,
    sm: immersiveActive ? 2 : 3,
    lg: immersiveActive ? 3 : 5
  })

  if (missingContributors)
    return (
      <CoverPageBlankSlateSection
        kind="contributors"
        title={t("immersive.nav.contributors")}
        sectionRef={contributorSectionRef}
        // Shaped like a contributor row: avatar, then name and bio.
        media={<Placeholder.Media boxSize="48px" borderRadius="full" />}
      />
    )
  if (!contributors.length) return null

  const contributorsHaveBios = contributors.some(
    (contributor: CoverPageContributorFragment) => !!contributor.bio
  )

  return (
    <Box position="relative" role="group" id="contributors">
      <CoverPageSectionTrigger sectionKey="contributors" top={"60px"} />
      {!immersiveActive && (
        <CoverPageDivider
          title={t("immersive.nav.contributors")}
          ref={contributorSectionRef}
        />
      )}
      {immersiveActive ? (
        <Box
          css={{
            "& .gallery-grid-item": {
              h: "100%"
            }
          }}
          m="0 auto"
          maxW={"1000px"}
        >
          <GridGallery
            items={contributors}
            renderItem={(contributor: CoverPageContributorFragment) => (
              <ContributorGridCard contributor={contributor} />
            )}
            gap={20}
            itemsFillWidth={false}
            spacingY="0"
            spacingX={0}
            itemsPerRow={itemsPerRow}
          />
        </Box>
      ) : contributorsHaveBios ? (
        <ContributorCardStack />
      ) : (
        <CarouselGallery
          items={contributors}
          renderItem={(contributor: CoverPageContributorFragment) => {
            return <ContributorCard contributor={contributor} full={false} />
          }}
          scrollBehavior={isMobile ? "smooth" : "snap"}
          gap={30}
          spacingY="0"
          itemsFillWidth={false}
          spacingX={isMobile ? COVER_PAGE_PADDING.base : COVER_PAGE_PADDING.md}
          itemsPerRow={itemsPerRow}
          // To the right of the last card, where another would join the row.
          trailing={<CoverPageAddAnother kind="contributors" />}
          enableStagger
        />
      )}
      {/* A stacked list grows downward, so the next one goes below it. */}
      {!immersiveActive && contributorsHaveBios ? (
        <Box display="flex" justifyContent="center" pt={2}>
          <CoverPageAddAnother kind="contributors" />
        </Box>
      ) : null}
    </Box>
  )
}

export default CoverPageContributors

// Immersive: Focused contributor view
export const CoverPageContributorImmersive = ({
  id
}: {
  id?: number | string
}) => {
  const { contributors, isMobile } = useCoverPageContext()

  if (!contributors.length) return null

  // Use the first contributor if no id is provided
  const normalizedId = !id
    ? contributors[0].id
    : typeof id === "string"
    ? Number(id)
    : id
  const contributor = contributors.find(
    (c: CoverPageContributorFragment) => c.id === normalizedId
  )

  if (!contributor) return null

  return (
    <Box
      key={`contributor-${id}`}
      w="100%"
      h="100%"
      pos="relative"
      mx="auto"
      px={0}
      className="CoverPageContributorImmersive"
      bgColor="backgroundSecondary"
      borderRadius="card"
      maxHeight="100%"
      overflow="auto"
      css={{
        "& p, div, span": {
          color: "primary"
        },
        "& .ContributorCard": {
          overflow: "auto",
          p: "30px"
        },
        "& .ContributorCardContent": {
          h: "auto"
        }
      }}
    >
      {/* Focused contributor: never truncate; row on desktop, column on mobile */}
      <ContributorCard
        contributor={contributor}
        full={!isMobile}
        disableTruncation
      />
    </Box>
  )
}
